import React from 'react';
import { View, Text, ScrollView, Platform, StyleSheet, Image } from 'react-native';
import { NotificationItem } from '@/hooks/common/useNotifications';
import { RESIDENTS_DESKTOP_COLORS } from '@/constants/residents/desktop/color';
import { NOTIFICATION_COLORS } from '@/constants/residents/mobile/notificationColors';
import { strings } from '@/constants/residents/strings';
import { StarRating } from '@/components/residents/marketplace';
import { formatDateMMMdd } from '@/utils/residents/date';

type Props = {
  selected: NotificationItem | null;
};

export const NotificationDetails: React.FC<Props> = ({ selected }) => {
  if (!selected) {
    return (
      <View style={styles.placeholder}>
        <Text style={styles.placeholderTitle}>{strings.notifications.desktop.placeholder.title}</Text>
        <Text style={styles.placeholderSub}>{strings.notifications.desktop.placeholder.subtitle}</Text>
      </View>
    );
  }

  // Check if this is a received feedback notification
  const isReceivedFeedback = selected.notificationType === 'marketplace_notification';
  
  if (isReceivedFeedback && selected.marketplaceNotification) {
    const metadata = selected.marketplaceNotification.metadata;
    const listingImageUrl = metadata?.listingImageUrl;
    const listingTitle = metadata?.listingTitle || '';
    const rating = metadata?.rating || 0;
    const reviewText = metadata?.reviewText || '';
    const buyerName = metadata?.buyerName || 'Unknown';
    const createdAt = selected.createdAt;

    return (
      <ScrollView contentContainerStyle={styles.detailContent} showsVerticalScrollIndicator={false}>
        {/* Header */}
        <Text style={styles.detailTitle}>{selected.title || 'Received feedback'}</Text>
        
        {/* Feedback Card */}
        <View style={styles.feedbackCard}>
          {/* Avatar + Content Row */}
          <View style={styles.feedbackRow}>
            {/* Listing Image */}
            <View style={styles.feedbackAvatar}>
              {listingImageUrl ? (
                <Image 
                  source={{ uri: listingImageUrl }} 
                  style={styles.feedbackAvatarImage} 
                  resizeMode="cover"
                />
              ) : (
                <View style={styles.feedbackAvatarPlaceholder} />
              )}
            </View>
            
            {/* Content */}
            <View style={styles.feedbackContent}>
              {/* Rating + Date Row */}
              <View style={styles.ratingDateRow}>
                <StarRating rating={rating} size={16} />
                <Text style={styles.feedbackDate}>{formatDateMMMdd(createdAt)}</Text>
              </View>
              
              {/* Listing Title */}
              <Text style={styles.feedbackListingTitle} numberOfLines={2}>
                {listingTitle}
              </Text>
              
              {/* Review Text */}
              {reviewText ? (
                <Text style={styles.feedbackReviewText}>
                  {reviewText}
                </Text>
              ) : null}
              
              {/* Buyer Name */}
              <Text style={styles.feedbackBuyerName}>
                {buyerName}
              </Text>
            </View>
          </View>
        </View>
      </ScrollView>
    );
  }

  // Check if there are image files to display
  const imageFiles = selected.files?.filter(file => {
    const url = file.fileUrl.toLowerCase();
    return url.includes('.jpg') || url.includes('.jpeg') || url.includes('.png') || 
           url.includes('.gif') || url.includes('.webp') || url.includes('.heic');
  }) || [];

  // Default layout for regular bulletins
  return (
    <ScrollView contentContainerStyle={styles.detailContent} showsVerticalScrollIndicator={false}>
      <Text style={styles.detailTitle}>{selected.title || selected.responseDescription || strings.notifications.desktop.defaultTitle}</Text>
      {!!selected.description && <Text style={styles.detailBody}>{selected.description}</Text>}
      {!!selected.descriptionHtml && (
        Platform.OS === 'web' ? (
          // eslint-disable-next-line react/no-danger
          (React.createElement('div', {
            style: { color: RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY, fontSize: 14 },
            dangerouslySetInnerHTML: { __html: selected.descriptionHtml },
          }))
        ) : (
          <Text style={styles.detailBody}>{selected.descriptionHtml}</Text>
        )
      )}
      
      {/* Render attached images */}
      {imageFiles.length > 0 && (
        <View style={styles.imagesContainer}>
          {imageFiles.map((file) => (
            <Image
              key={file.id}
              source={{ uri: file.fileUrl }}
              style={styles.attachedImage}
              resizeMode="cover"
            />
          ))}
        </View>
      )}
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  placeholder: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
  },
  placeholderTitle: { color: RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY, fontWeight: '800' },
  placeholderSub: { color: RESIDENTS_DESKTOP_COLORS.TEXT_TERTIARY },
  detailContent: { padding: 20, gap: 10 },
  detailTitle: { color: RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY, fontWeight: '800', fontSize: 18 },
  detailBody: { color: RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY, fontSize: 14 },
  imagesContainer: {
    marginTop: 16,
    gap: 12,
  },
  attachedImage: {
    width: '100%',
    height: 200,
    borderRadius: 8,
    backgroundColor: RESIDENTS_DESKTOP_COLORS.SECONDARY_BACKGROUND,
  },
  
  // Received Feedback styles
  feedbackCard: {
    marginTop: 10,
    paddingBottom: 16,
    borderBottomWidth: 1,
    borderBottomColor: NOTIFICATION_COLORS.borderSecondary,
  },
  feedbackRow: {
    flexDirection: 'row',
    gap: 16,
  },
  feedbackAvatar: {
    width: 46,
    height: 46,
    borderRadius: 23,
    overflow: 'hidden',
    backgroundColor: NOTIFICATION_COLORS.cardBackground,
  },
  feedbackAvatarImage: {
    width: 46,
    height: 46,
    borderRadius: 23,
  },
  feedbackAvatarPlaceholder: {
    width: 46,
    height: 46,
    borderRadius: 23,
    backgroundColor: NOTIFICATION_COLORS.cardBackground,
  },
  feedbackContent: {
    flex: 1,
    gap: 4,
  },
  ratingDateRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  feedbackDate: {
    fontSize: 12,
    fontWeight: '300',
    color: NOTIFICATION_COLORS.textSecondary,
  },
  feedbackListingTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: NOTIFICATION_COLORS.textPrimary,
    lineHeight: 21,
  },
  feedbackReviewText: {
    fontSize: 14,
    fontWeight: '400',
    color: NOTIFICATION_COLORS.textSecondary,
    lineHeight: 21,
  },
  feedbackBuyerName: {
    fontSize: 12,
    fontWeight: '400',
    color: NOTIFICATION_COLORS.textSecondary,
  },
});

export default NotificationDetails;


